import {
  SelectedFlight,
  SelectedTicket,
  SESSION_KEYS,
  SelectedHotel,
} from 'app/pages/HolidayList/types';

export const setSessionItem = <T>(key: string, value: T): void => {
  try {
    const serialized = JSON.stringify(value);
    sessionStorage.setItem(key, serialized);
  } catch (error) {
    console.error(`Error saving to sessionStorage key "${key}":`, error);
  }
};

export const getSessionItem = <T>(key: string): T | null => {
  try {
    const item = sessionStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error(`Error reading from sessionStorage key "${key}":`, error);
    return null;
  }
};

export const removeSessionItem = (key: string): void => {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing sessionStorage key "${key}":`, error);
  }
};

export const clearSessionItems = (keys: string[]): void => {
  keys.forEach(key => removeSessionItem(key));
};

// Flight Selection Operations
export const saveSelectedFlight = (flight: SelectedFlight): void => {
  const existingFlights = getSelectedFlights();
  // Remove existing flight for same route if present (to replace with new selection)
  const filtered = existingFlights.filter(
    f => f.route.route !== flight.route.route,
  );
  const updatedFlights = [...filtered, flight];
  setSessionItem(SESSION_KEYS.SELECTED_FLIGHTS, updatedFlights);
};

export const getSelectedFlights = (): SelectedFlight[] => {
  return getSessionItem<SelectedFlight[]>(SESSION_KEYS.SELECTED_FLIGHTS) || [];
};

export const clearSelectedFlights = (): void => {
  removeSessionItem(SESSION_KEYS.SELECTED_FLIGHTS);
};

export const removeFlightByRoute = (route: string): void => {
  const flights = getSelectedFlights();
  const filtered = flights.filter(f => f.route.route !== route);
  setSessionItem(SESSION_KEYS.SELECTED_FLIGHTS, filtered);
};

// Ticket Selection Operations
export const saveSelectedTicket = (ticket: SelectedTicket): void => {
  const existingTickets = getSelectedTickets();
  // Remove existing ticket with same ID if present
  const filtered = existingTickets.filter(
    t => t.ticket.id !== ticket.ticket.id,
  );

  // Ensure the stored ticket object always includes a proper "totalPrice" field
  // favouring the value returned by the combo search API, but fall back to
  // paxPrice or price if necessary. Also keep paxPrice in sync for convenience.
  const orig = ticket.ticket as any;
  const normalized: any = {
    ...orig,
    totalPrice: orig.totalPrice ?? orig.paxPrice ?? orig.price ?? 0,
    paxPrice: orig.paxPrice ?? orig.totalPrice ?? orig.price ?? 0,
  };

  const updatedTickets = [...filtered, { ...ticket, ticket: normalized }];
  setSessionItem(SESSION_KEYS.SELECTED_TICKETS, updatedTickets);
};

export const getSelectedTickets = (): SelectedTicket[] => {
  const raw =
    getSessionItem<SelectedTicket[]>(SESSION_KEYS.SELECTED_TICKETS) || [];
  // ensure each ticket has proper totalPrice/paxPrice for backwards compatibility
  return raw.map(t => {
    const orig = t.ticket as any;
    const normalized: any = {
      ...orig,
      totalPrice: orig.totalPrice ?? orig.paxPrice ?? orig.price ?? 0,
      paxPrice: orig.paxPrice ?? orig.totalPrice ?? orig.price ?? 0,
    };
    return { ...t, ticket: normalized };
  });
};

export const clearSelectedTickets = (): void => {
  removeSessionItem(SESSION_KEYS.SELECTED_TICKETS);
};
export const saveSelectedHotel = (hotel: SelectedHotel): void => {
  const existingHotels = getSelectedHotels();
  const filtered = existingHotels.filter(h => h.hotel.id !== hotel.hotel.id);
  const updatedHotels = [...filtered, hotel];
  setSessionItem(SESSION_KEYS.SELECTED_HOTELS, updatedHotels);
};
export const saveArrayHotel = (hotels: SelectedHotel[]): void => {
  clearSelectedHotels();
  setSessionItem(SESSION_KEYS.SELECTED_HOTELS, hotels);
};
export const getSelectedHotels = (): SelectedHotel[] => {
  return getSessionItem<SelectedHotel[]>(SESSION_KEYS.SELECTED_HOTELS) || [];
};
export const clearSelectedHotels = (): void => {
  removeSessionItem(SESSION_KEYS.SELECTED_HOTELS);
};

export const removeTicketById = (ticketId: string): void => {
  const tickets = getSelectedTickets();
  const filtered = tickets.filter(t => t.ticket.id !== ticketId);
  setSessionItem(SESSION_KEYS.SELECTED_TICKETS, filtered);
};

// Session ID Operations
export const saveComboSessionId = (sessionId: string): void => {
  setSessionItem(SESSION_KEYS.COMBO_SESSION_ID, sessionId);
};

export const getComboSessionId = (): string | null => {
  return getSessionItem<string>(SESSION_KEYS.COMBO_SESSION_ID);
};

export const clearComboSessionId = (): void => {
  removeSessionItem(SESSION_KEYS.COMBO_SESSION_ID);
};

// Search Params Operations
export const saveSearchParams = (params: any): void => {
  setSessionItem(SESSION_KEYS.SEARCH_PARAMS, params);
};

export const getSearchParams = (): any | null => {
  return getSessionItem(SESSION_KEYS.SEARCH_PARAMS);
};

export const clearSearchParams = (): void => {
  removeSessionItem(SESSION_KEYS.SEARCH_PARAMS);
};

// Passenger info form operations (passengers + contact only)
export const savePassengerInfoForm = (data: {
  passengers?: any[];
  contact?: any;
}): void => {
  setSessionItem(SESSION_KEYS.PASSENGER_INFO_FORM, data);
};

export const getPassengerInfoForm = (): {
  passengers: any[];
  contact: any | null;
} => {
  const data = getSessionItem<any>(SESSION_KEYS.PASSENGER_INFO_FORM);
  return {
    passengers: data?.passengers || [],
    contact: data?.contact || null,
  };
};

export const clearPassengerInfoForm = (): void => {
  removeSessionItem(SESSION_KEYS.PASSENGER_INFO_FORM);
};

// Special request operations
export const saveSpecialRequest = (request: string): void => {
  setSessionItem(SESSION_KEYS.SPECIAL_REQUEST, request);
};

export const getSpecialRequest = (): string => {
  return getSessionItem<string>(SESSION_KEYS.SPECIAL_REQUEST) || '';
};

export const clearSpecialRequest = (): void => {
  removeSessionItem(SESSION_KEYS.SPECIAL_REQUEST);
};

// Clear All Holiday Data
export const clearAllHolidayData = (): void => {
  clearSessionItems([
    SESSION_KEYS.SELECTED_FLIGHTS,
    SESSION_KEYS.SELECTED_TICKETS,
    SESSION_KEYS.SELECTED_HOTELS,
    SESSION_KEYS.COMBO_SESSION_ID,
    SESSION_KEYS.SEARCH_PARAMS,
    SESSION_KEYS.PASSENGER_INFO_FORM,
    SESSION_KEYS.SPECIAL_REQUEST,
  ]);
};

// Get Summary of All Selections
export const getHolidaySelectionSummary = () => {
  const passengerInfo = getPassengerInfoForm();
  const specialRequest = getSpecialRequest();
  return {
    flights: getSelectedFlights(),
    tickets: getSelectedTickets(),
    hotels: getSelectedHotels(),
    sessionId: getComboSessionId(),
    searchParams: getSearchParams(),
    passengerInfo,
    specialRequest,
  };
};
